<script setup lang="ts">
import type { IDailyTemperatureSensor, IDailyTemperatureTableSensor } from '@/interfaces/entities/daily-temperature'
import {
  cellClass,
  fmtSetpoint,
  fmtTemperature,
  hasSetpoint,
} from '@/utils/daily-temperature/period-filter'
import { useI18n } from '@/composable/useI18n'
import { computed } from 'vue'

const props = defineProps<{
  hours: string[]
  tableSensors: IDailyTemperatureTableSensor[]
  isCompareEnabled: boolean
  primaryDateShort: string
  compareDateShort: string
  primarySensorById: Record<number, IDailyTemperatureSensor>
  compareSensorById: Record<number, IDailyTemperatureSensor>
}>()

const { t } = useI18n()

const SENSOR_COL_WIDTH = '10rem'
const HOUR_COL_MIN = '3rem'
const COMPARE_HOUR_COL_MIN = '2.75rem'

const tableMinWidth = computed(() => {
  if (props.isCompareEnabled) {
    return `calc(${SENSOR_COL_WIDTH} + ${props.hours.length} * 2 * ${COMPARE_HOUR_COL_MIN})`
  }
  return `calc(${SENSOR_COL_WIDTH} + ${props.hours.length} * ${HOUR_COL_MIN})`
})

const stickySensorHeaderClass =
  'sticky left-0 z-30 w-[10rem] min-w-[10rem] max-w-[10rem] border border-gray-200 bg-emerald-800 px-3 py-2 text-left font-semibold text-white shadow-[4px_0_8px_-4px_rgba(15,23,42,0.25)]'

const hourHeaderClass =
  'border border-gray-200 bg-emerald-800 px-1 py-2 font-semibold text-white min-w-[3rem]'

const compareHourHeaderClass =
  'border border-gray-200 bg-emerald-800 px-1 py-2 font-semibold text-white min-w-[2.75rem]'

const compareDateHeaderClass =
  'border border-gray-200 bg-gray-2 px-1 py-1.5 font-medium text-slate-700 min-w-[2.75rem]'

function stickySensorCellClass(rowIndex: number): string {
  const rowBg = rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
  return `sticky left-0 z-20 w-[10rem] min-w-[10rem] max-w-[10rem] border border-gray-200 px-3 py-2 text-left font-medium text-slate-800 shadow-[4px_0_8px_-4px_rgba(15,23,42,0.12)] ${rowBg}`
}

function rowClass(rowIndex: number): string {
  return rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
}

function getPrimarySensor(sensorId: number): IDailyTemperatureSensor | undefined {
  return props.primarySensorById[sensorId]
}

function getCompareSensor(sensorId: number): IDailyTemperatureSensor | undefined {
  return props.compareSensorById[sensorId]
}

function primaryCellClass(sensorId: number, hour: string): string {
  const sensor = getPrimarySensor(sensorId)
  if (!sensor) return 'text-gray-300'
  return cellClass(sensor, hour)
}

function compareCellClass(sensorId: number, hour: string): string {
  const sensor = getCompareSensor(sensorId)
  if (!sensor) return 'bg-gray-2 text-gray-300'
  const base = cellClass(sensor, hour)
  if (base.includes('bg-red-500')) return base
  return `bg-gray-2 ${base}`
}
</script>

<template>
  <div class="w-full overflow-x-auto bg-white shadow-sm">
    <table
      class="w-full table-fixed border-separate border-spacing-0 text-center text-xs"
      :style="{ minWidth: tableMinWidth }">
      <colgroup>
        <col class="w-[10rem]" />
        <template v-if="!isCompareEnabled">
          <col v-for="hour in hours" :key="`col-${hour}`" />
        </template>
        <template v-else>
          <template v-for="hour in hours" :key="`cols-${hour}`">
            <col />
            <col />
          </template>
        </template>
      </colgroup>

      <thead v-if="!isCompareEnabled">
        <tr>
          <th :class="stickySensorHeaderClass">
            {{ t('daily_temperature.table.sensor') }}
          </th>
          <th v-for="hour in hours" :key="hour" :class="hourHeaderClass">
            {{ hour }}
          </th>
        </tr>
      </thead>

      <thead v-else>
        <tr>
          <th rowspan="2" :class="stickySensorHeaderClass">
            {{ t('daily_temperature.table.sensor') }}
          </th>
          <th
            v-for="hour in hours"
            :key="`hour-${hour}`"
            colspan="2"
            :class="compareHourHeaderClass">
            {{ hour }}
          </th>
        </tr>
        <tr>
          <template v-for="hour in hours" :key="`dates-${hour}`">
            <th :class="compareHourHeaderClass">
              {{ primaryDateShort }}
            </th>
            <th :class="compareDateHeaderClass">
              {{ compareDateShort }}
            </th>
          </template>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(sensor, rowIndex) in tableSensors"
          :key="sensor.id"
          :class="rowClass(rowIndex)">
          <td :class="stickySensorCellClass(rowIndex)">
            {{ sensor.name }}
            <span v-if="hasSetpoint(sensor)" class="block text-[10px] font-normal text-slate-400">
              {{
                t('daily_temperature.legend.setpoint_label', {
                  value: fmtSetpoint(sensor.setpoint),
                })
              }}
            </span>
          </td>

          <template v-if="!isCompareEnabled">
            <td
              v-for="hour in hours"
              :key="`${sensor.id}-${hour}`"
              class="min-w-[3rem] border border-gray-200 px-1 py-2 tabular-nums"
              :class="primaryCellClass(sensor.id, hour)">
              {{ fmtTemperature(getPrimarySensor(sensor.id)?.values?.[hour]) }}
            </td>
          </template>

          <template v-else>
            <template v-for="hour in hours" :key="`${sensor.id}-${hour}-pair`">
              <td
                class="min-w-[2.75rem] border border-gray-200 px-1 py-2 tabular-nums"
                :class="primaryCellClass(sensor.id, hour)">
                {{ fmtTemperature(getPrimarySensor(sensor.id)?.values?.[hour]) }}
              </td>
              <td
                class="min-w-[2.75rem] border border-gray-200 px-1 py-2 tabular-nums"
                :class="compareCellClass(sensor.id, hour)">
                {{ fmtTemperature(getCompareSensor(sensor.id)?.values?.[hour]) }}
              </td>
            </template>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
</template>
